import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, KeyRound, LayoutGrid, LogOut, UserRound } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { usePersonalCenter } from "../data/PersonalCenterContext";
import { setOpsTenantId } from "../api/client";

type UserAccountMenuProps = {
  className?: string;
  /** 调试工作台：提供项目 id 时显示「退出工作台」 */
  exitWorkbenchProjectId?: string | null;
  /** 运营跨租户调试：退出回到运营项目列表并清除租户绑定 */
  exitWorkbenchTo?: string | null;
};

export function UserAccountMenu({
  className = "",
  exitWorkbenchProjectId = null,
  exitWorkbenchTo = null,
}: UserAccountMenuProps) {
  const { email, name, org, logout, role } = useAuth();
  const { openPersonalCenter } = usePersonalCenter();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isWxPlaceholder = Boolean(email?.endsWith("@wechat.udhub.local"));
  const displayName =
    (name || "").trim() ||
    (isWxPlaceholder ? "微信用户" : email?.split("@")[0]) ||
    "工程师";
  const secondaryLabel = isWxPlaceholder
    ? "微信登录账号"
    : email || "";
  const avatarLetter = (displayName[0] || "U").toUpperCase();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div className={`topbar-user ${className}`.trim()} ref={ref}>
      <button
        className="user-trigger"
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="avatar">{avatarLetter}</div>
        <div className="user-meta">
          <strong>{displayName}</strong>
          <span>{secondaryLabel}</span>
        </div>
        <ChevronDown size={14} className={open ? "open" : ""} />
      </button>
      {open ? (
        <div className="user-dropdown" role="menu">
          <div className="user-dropdown-org">
            <div className="org-name">{org.shortName}</div>
            <div className="org-hint">{org.name} · 组织切换器 ⌄</div>
          </div>
          <div className="user-dropdown-divider" />
          <button
            className="user-dropdown-item"
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              openPersonalCenter();
            }}
          >
            <UserRound size={15} />
            个人中心
          </button>
          <button
            className="user-dropdown-item"
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              navigate("/settings/password");
            }}
          >
            <KeyRound size={15} />
            修改密码
          </button>
          {exitWorkbenchProjectId ? (
            <button
              className="user-dropdown-item"
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                const opsHome =
                  exitWorkbenchTo ||
                  (role === "platform_admin" ? "/admin/projects" : null);
                if (opsHome) {
                  setOpsTenantId(null);
                  navigate(opsHome);
                  return;
                }
                navigate(`/projects/${exitWorkbenchProjectId}`);
              }}
            >
              <LayoutGrid size={15} />
              {role === "platform_admin" ? "返回运营项目调试" : "退出工作台"}
            </button>
          ) : null}
          <button
            className="user-dropdown-item"
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              logout();
            }}
          >
            <LogOut size={15} />
            退出登录
          </button>
        </div>
      ) : null}
    </div>
  );
}
